/**
 * Small-order fee manager (cart drawer, product pages, quick add, upsells)
 *
 * - Products total (after discounts, excluding fee / gift wrap / shipping protection) < $50
 *     -> fee product added, locked at quantity 1
 * - Products total >= $50, or no real products in cart
 *     -> fee product removed
 *
 * The cart PAGE is handled by CartItems.checkCartAndManageVariant() in cart.js,
 * so this script does nothing while <cart-items> is on the page.
 *
 * Keep FEE_VARIANT_ID / THRESHOLD in sync with CartItems in cart.js.
 */
(() => {
  if (window.__smallOrderFeeLoaded) return;
  window.__smallOrderFeeLoaded = true;

  const FEE_VARIANT_ID = 67765593669996;
  const THRESHOLD = 5000; // in cents -> $50.00
  const DEBOUNCE_MS = 500; // wait for the theme to finish its own drawer render

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);

  let timer = null;
  let busy = false;
  let recheck = false;

  /* ---------- helpers ---------- */

  const onCartPage = () => !!document.querySelector("cart-items");

  const getCart = () =>
    nativeFetch(`${root}cart.js`, { headers: { Accept: "application/json" } }).then((r) =>
      r.json()
    );

  // Fee + add-ons that should not count toward the $50
  function excludedVariantIds() {
    const ids = new Set([FEE_VARIANT_ID]);
    document
      .querySelectorAll('[id^="gift_form"][data-variant-id], [id^="shipping_protection_form"][data-variant-id]')
      .forEach((el) => {
        const id = Number(el.dataset.variantId);
        if (id) ids.add(id);
      });
    return ids;
  }

  // Uses the native fetch so our own requests don't re-trigger the watcher
  async function cartRequest(path, payload) {
    const res = await nativeFetch(`${root}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || "Cart request failed");
    }
    return data;
  }

  /* ---------- UI refresh ---------- */

  function updateCounts(cart) {
    document.querySelectorAll(".cart-count").forEach((el) => {
      el.innerHTML = el.classList.contains("cart-count-drawer")
        ? `(${cart.item_count})`
        : cart.item_count > 100
        ? "~"
        : cart.item_count;
    });

    const headerTotal = document.querySelector("header-total-price");
    if (headerTotal && typeof headerTotal.updateTotal === "function") {
      headerTotal.updateTotal(cart);
    }

    const freeShip = document.querySelector("free-ship-progress-bar");
    if (freeShip && typeof freeShip.init === "function") {
      freeShip.init(cart.items_subtotal_price);
    }
  }

  // Section that contains the drawer (#minicart_wrapper)
  function drawerSectionId() {
    const wrapper = document.getElementById("minicart_wrapper");
    const section = wrapper && wrapper.closest(".shopify-section");
    return section ? section.id.replace(/^shopify-section-/, "") : null;
  }

  async function refreshDrawer(cart) {
    updateCounts(cart);

    // A customer's change is still on its way and its response redraws the
    // drawer, fee included. Redrawing here would reset the quantity they set.
    const drawer = document.querySelector("cart-notification");
    if (drawer && drawer.hasQueuedQuantityChanges && drawer.hasQueuedQuantityChanges()) return;

    const current = document.getElementById("minicart-form");
    if (!current) return; // drawer not on this page

    const sectionId = drawerSectionId();
    const url = sectionId
      ? `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`
      : `${window.location.pathname}${window.location.search}`;

    const res = await nativeFetch(url);
    const html = sectionId ? (await res.json())[sectionId] : await res.text();
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, "text/html");

    if (cart.item_count === 0) {
      // Cart emptied -> swap whole drawer body to show the empty state
      const fresh = doc.getElementById("minicart-form");
      if (fresh) current.innerHTML = fresh.innerHTML;
    } else {
      // Only replace the line items + totals (keeps gift wrap / protection / terms listeners)
      [
        "#minicart",
        "#minicart-form [data-cart-summary]",
        "#minicart-form [data-cart-subtotal-price]",
      ].forEach((selector) => {
        const from = doc.querySelector(selector);
        const to = document.querySelector(selector);
        if (from && to) to.innerHTML = from.innerHTML;
      });
    }

    if (window.BlsLazyloadImg && typeof BlsLazyloadImg.init === "function") {
      BlsLazyloadImg.init();
    }
  }

  /* ---------- fee logic ---------- */

  async function checkFee() {
    if (onCartPage()) return; // cart.js handles the cart page

    if (busy) {
      recheck = true;
      return;
    }
    busy = true;

    try {
      const cart = await getCart();
      const excluded = excludedVariantIds();

      const feeItem = cart.items.find((item) => Number(item.variant_id) === FEE_VARIANT_ID);
      const productItems = cart.items.filter((item) => !excluded.has(Number(item.variant_id)));
      const excludedTotal = cart.items
        .filter((item) => excluded.has(Number(item.variant_id)))
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const productsTotal = Math.max(0, cart.total_price - excludedTotal);

      const needsFee = productItems.length > 0 && productsTotal < THRESHOLD;

      let changed = false;
      if (!needsFee && feeItem) {
        await cartRequest("cart/change.js", { id: feeItem.key, quantity: 0 });
        changed = true;
      } else if (needsFee && !feeItem) {
        await cartRequest("cart/add.js", { items: [{ id: FEE_VARIANT_ID, quantity: 1 }] });
        changed = true;
      } else if (needsFee && feeItem.quantity !== 1) {
        await cartRequest("cart/change.js", { id: feeItem.key, quantity: 1 });
        changed = true;
      }

      if (changed) {
        await refreshDrawer(await getCart());
      }
    } catch (error) {
      console.error("Small-order fee check failed:", error);
    } finally {
      busy = false;
      if (recheck) {
        recheck = false;
        scheduleCheck();
      }
    }
  }

  // Takes its turn with the drawer's own cart changes so they can't overlap
  function runCheck() {
    const drawer = document.querySelector("cart-notification");
    if (drawer && drawer.enqueueCartTask) {
      drawer.enqueueCartTask(checkFee);
    } else {
      checkFee();
    }
  }

  function scheduleCheck() {
    clearTimeout(timer);
    timer = setTimeout(runCheck, DEBOUNCE_MS);
  }

  // Expose for manual calls, e.g. window.SmallOrderFee.check()
  window.SmallOrderFee = { check: scheduleCheck, FEE_VARIANT_ID, THRESHOLD };

  /* ---------- watch every cart change made by the theme ---------- */

  window.fetch = function (input, init) {
    const url = typeof input === "string" ? input : (input && input.url) || "";
    const request = nativeFetch(input, init);

    if (CART_MUTATION.test(url)) {
      request
        .then((response) => {
          if (response.ok) scheduleCheck();
        })
        .catch(() => {});
    }

    return request;
  };

  // Check once on page load (cart may already be under $50)
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleCheck);
  } else {
    scheduleCheck();
  }
})();